import { useEffect, useMemo, useState } from 'react';
import { useGetUser } from '@/queries/user';
import { useQuery } from 'react-query';
import { requestNextApi } from '@/utils/next-api';
import Toast from '@/components/Toast';
import { useIsPasscodeSupported } from '@/modules/auth/hooks/Passcode';
import {
  createCredential,
  getCredential,
  isAuthenticatorSupported,
  TCreateCredentialReturn,
  TGetCredentialReturn,
} from '@/modules/auth/utils/credential';

type THasAuthenticatorData = {
  hasAuthenticator: boolean;
  uid: string;
  credentialIds: string[];
};

export const useIsAuthenticatorSupported = () => {
  const isPasscodeSupported = useIsPasscodeSupported();
  const [isSupported, setIsSupported] = useState<boolean>(false);

  useEffect(() => {
    isAuthenticatorSupported().then(setIsSupported);
  }, []);

  return isSupported && isPasscodeSupported;
};

export const getNewChallenge = async () => {
  return undefined;
  //TODO: هرموقع وریفای سمت سرور رو فعال کردیم اینو هم باید آنکامنت کنیم
  // return requestNextApi<string>('auth/passcode/authenticator/challenge');
};

export const useHasAuthenticator = () => {
  const isAuthenticatorSupported = useIsAuthenticatorSupported();

  const result = useQuery<THasAuthenticatorData>(
    ['___', 'authenticator'],
    async (): Promise<THasAuthenticatorData> => {
      return requestNextApi<THasAuthenticatorData>(
        'auth/passcode/authenticator'
      );
    },
    {
      enabled: isAuthenticatorSupported,
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['isFetching', 'data'],
    }
  );

  return useMemo(() => {
    const { isFetching, refetch } = result;
    const data = result?.data ?? ({} as THasAuthenticatorData);

    return {
      ...data,
      data,
      refetch,
      isLoading: isFetching,
      isAuthenticatorSupported,
    };
  }, [isAuthenticatorSupported, result]);
};

export const useAuthenticator = () => {
  const { data: user } = useGetUser();

  const [isMutationLoading, setIsMutationLoading] = useState<boolean>(false);

  const {
    data,
    refetch,
    hasAuthenticator,
    isLoading: isFetchLoading,
  } = useHasAuthenticator();

  const getData = async () => {
    const [{ uid, credentialIds }, challenge] = await Promise.all([
      (data ?? (await refetch()).data ?? {}) as THasAuthenticatorData,
      getNewChallenge(),
    ]);
    return { uid, credentialIds, challenge };
  };

  const add = async () => {
    setIsMutationLoading(true);
    try {
      const { uid, credentialIds, challenge } = await getData();

      const credential: TCreateCredentialReturn = await createCredential(
        {
          id: `${uid}`,
          name: `${user?.nationalId}`,
          displayName: `${user?.displayName}`,
        },
        { challenge, credentialIds }
      );

      await requestNextApi('auth/passcode/authenticator/add', {
        method: 'POST',
        data: credential,
      });

      refetch().then();
    } catch (e) {
      Toast.open({ variant: 'error', children: 'ثبت انجام نشد' });
    } finally {
      setIsMutationLoading(false);
    }
  };

  const remove = async () => {
    setIsMutationLoading(true);
    try {
      await requestNextApi('auth/passcode/authenticator/remove', {
        method: 'DELETE',
      });

      refetch().then();
    } catch (e) {
      Toast.open({ variant: 'error', children: 'حذف انجام نشد' });
    } finally {
      setIsMutationLoading(false);
    }
  };

  const verify = async () => {
    const { credentialIds, challenge } = await getData();

    const assertion: TGetCredentialReturn = await getCredential({
      challenge,
      credentialIds,
    });

    await requestNextApi('auth/passcode/authenticator/verify', {
      method: 'POST',
      data: assertion,
    });
  };

  return {
    hasAuthenticator,
    add,
    remove,
    verify,
    refetch,
    isLoading: isMutationLoading || isFetchLoading,
  };
};
